import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Plus, FolderOpen } from 'lucide-react';
import { useProjectDocuments, useUploadProjectDocument } from '@/hooks/use-projects';
import { handleApiError, resolveMediaUrl } from '@/lib/api-client';
import { toast } from 'sonner';

export function DocumentsTab({ projectId }: { projectId: number }) {
  const { data: documents } = useProjectDocuments(projectId);
  const uploadDoc = useUploadProjectDocument();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('document_type', 'other');
    try {
      await uploadDoc.mutateAsync({ projectId, data: formData });
      toast.success('Document uploaded');
    } catch (err) {
      const fieldError = Object.entries(handleApiError(err).errors ?? {}).find(
        ([, msgs]) => msgs.length > 0
      );
      toast.error(
        fieldError ? `Upload failed: ${fieldError[0]}: ${fieldError[1][0]}` : 'Upload failed'
      );
    } finally {
      // Clear so choosing the same file again fires onChange.
      input.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Documents ({documents?.length ?? 0})</h3>
        <Button size="sm" asChild>
          <label className="cursor-pointer">
            <Plus className="h-4 w-4 mr-1" />
            Upload
            <input type="file" className="hidden" onChange={handleUpload} />
          </label>
        </Button>
      </div>

      <div className="space-y-2">
        {documents?.map((doc) => (
          <Card key={doc.id}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FolderOpen className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium">{doc.name}</p>
                  <p className="text-xs text-gray-500">
                    {doc.document_type} &middot; Uploaded {doc.created_at}
                  </p>
                </div>
              </div>
              {doc.file && (
                <Button variant="outline" size="sm" asChild>
                  <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
        {(!documents || documents.length === 0) && (
          <p className="text-gray-500 text-center py-8">No documents uploaded</p>
        )}
      </div>
    </div>
  );
}

